import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import qs, { IParseOptions, IStringifyOptions } from 'qs';
import { WorkItemStateShort } from '~/types/resources';
import { useParams } from './useParams';

const qsStringifyOptions: IStringifyOptions = {
  skipNulls: true,
};

const qsParseOptions: IParseOptions = {
  ignoreQueryPrefix: true,
};

const serializeQueryString = <T>(value: T) => {
  try {
    return qs.stringify(value, qsStringifyOptions);
  } catch (_error) {
    throw new Error('failed to serialize query entry');
  }
};

const deserializeQueryString = (value: string) => {
  try {
    return qs.parse(value, qsParseOptions);
  } catch {
    return {};
  }
};

type DataExplorerConfigInput = {
  activityId?: string[] | undefined;
  state?: WorkItemStateShort[] | undefined;
};

const prepareSearchParams = (search?: string, filters?: DataExplorerConfigInput) =>
  serializeQueryString({
    ...deserializeQueryString(search ?? window.location.search),
    workItems: { filters },
  });

export const useDataExplorerLink = () => {
  const { search } = useLocation();

  const createExplorerLink = useCallback(
    ({
      to: toSuffix,
      pathPrefix = '',
      ...filters
    }: DataExplorerConfigInput & { to?: string; pathPrefix?: string }) => {
      const searchParams = prepareSearchParams(
        search,
        Object.keys(filters).length > 0 ? filters : undefined,
      );

      if (toSuffix) {
        return `${pathPrefix}explorer/${toSuffix}?${searchParams}`;
      }

      return `${pathPrefix}explorer?${searchParams}`;
    },
    [search],
  );

  return createExplorerLink;
};

export const useDataExplorer = () => {
  const navigate = useNavigate();
  const { processId, pRunId, stepRunId, workItemId } = useParams<'processId'>();

  const createExplorerLink = useDataExplorerLink();

  const setExplorerData = useCallback(
    (action: DataExplorerConfigInput & { to?: string; pathPrefix?: string }) => {
      createExplorerLink(action);
      return navigate(createExplorerLink(action));
    },
    [createExplorerLink, navigate],
  );

  const config = useMemo(
    () => ({
      scope: (pRunId === undefined ? 'process' : 'processRun') as 'process' | 'processRun',
      pId: processId,
      pRunId,
      activityRunId: stepRunId,
      workItemId,
    }),
    [pRunId, processId, stepRunId, workItemId],
  );

  return [setExplorerData, config] as const;
};
